import { DataTable } from "@/components/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { columns } from "../_misc/columns";
import { useGetCardTransactions, useGetDeposits } from "@/hooks/use-queries";
import TopBar from "@/components/topbar";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";

const Transactions = () => {
  const [tab, setTab] = useState<"wallet" | "cards">("wallet");
  const [paginatedData, setPaginationData] = useState([]);
  const [paginationParams, setPaginationParams] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [pageCount, setPageCount] = useState(1);
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetDeposits({
      page: paginationParams.pageIndex + 1,
      limit: paginationParams.pageSize,
    });
  const { data: cardTransactions, isLoading: isLoadingCardTransactions } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });

  const handleTabChange = (value) => {
    setTab(value);
    setPaginationParams({
      pageIndex: 0,
      pageSize: 10,
    });
  };

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= pageCount) {
      setPaginationParams({
        pageIndex: page - 1,
        pageSize: paginationParams.pageSize,
      });
    }
  };

  useEffect(() => {
    if (tab === "wallet") {
      setPageCount(transactions?.totalPages || 1);
    } else {
      setPageCount(
        Math.ceil((cardTransactions?.total || 0) / paginationParams.pageSize) ||
          1,
      );
    }
  }, [tab, transactions, cardTransactions, paginationParams.pageSize]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (tab === "cards") {
      setPaginationData(
        cardTransactions?.data.slice(
          paginationParams.pageIndex * 10,
          (paginationParams.pageIndex + 1) * 10,
        ),
      );
    }
  }, [cardTransactions, paginationParams]);

  return (
    <div className="">
      <TopBar title="Transactions" />
      <Tabs
        onValueChange={(value: "wallet" | "cards") => {
          handleTabChange(value);
        }}
        value={tab}
        className="font-poppins px-4 py-4 lg:bg-transparent"
      >
        <TabsList className="border-dark-stroke-5 bg-dark-background-secondary w-full border p-0 lg:w-fit">
          <TabsTrigger
            value="wallet"
            className="text-dark-text-300 data-[state=active]:bg-dark-primary-gradient data-[state=active]:text-dark-text-400 rounded-l-lg rounded-r-none px-6"
          >
            Wallet
          </TabsTrigger>
          <TabsTrigger
            value="cards"
            className="text-dark-text-300 data-[state=active]:bg-dark-primary-gradient data-[state=active]:text-dark-text-400 rounded-l-none rounded-r-lg px-6"
          >
            Cards
          </TabsTrigger>
        </TabsList>
        <div className="bg-dark-card-3 text-dark-text-50 mt-4 rounded-2xl p-4 backdrop-blur-sm lg:row-start-auto">
          <div className="flex items-center justify-between bg-white/5"></div>

          <TabsContent value="wallet" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={columns}
                data={transactions?.data ?? []}
                isLoading={isLoadingTransactions}
                pagination={paginationParams}
                setPagination={setPaginationParams}
                pageCount={pageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingTransactions}
                transactions={transactions?.data ?? []}
                currentPage={paginationParams.pageIndex + 1}
                totalPages={pageCount}
                handlePageChange={handlePageChange}
              />
            </div>
          </TabsContent>
          <TabsContent value="cards" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={paginatedData ?? []}
                isLoading={isLoadingCardTransactions}
                pagination={paginationParams}
                setPagination={setPaginationParams}
                pageCount={pageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingCardTransactions}
                transactions={paginatedData ?? []}
                type="cards"
                currentPage={paginationParams.pageIndex + 1}
                totalPages={pageCount}
                handlePageChange={handlePageChange}
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default Transactions;
